import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Grid,
    GridColumn as Column,
    GridToolbar,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../LoadingPanel";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { CommandCell } from '../forms/CommandCell';
import { DropDownCell } from '../forms/DropDownCell';
import { getCSRFToken } from '../../app/getCSRFToken';

export const AdministratorSystemSettings = () => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const [settingTypes, setSettingTypes] = useState([]);
    const [settings, setSettings] = useState([]);
    const [originalSettings, setOriginalSettings] = useState([]);
    const [areSettingsLoading, setAreSettingsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");

    const getSettingTypes = () => {
        fetch(`/api/administrator/settingtypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setSettingTypes(result);

                getSettings();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreSettingsLoading(false);
                setLoadingError(error.message);
            });
    }

    const getSettings = () => {
        setAreSettingsLoading(true);

        fetch(`/api/administrator/settings`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setAreSettingsLoading(false);
                setSettings(result.settings);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreSettingsLoading(false);
                setLoadingError(error.message);
            });
    }

    const addSetting = (dataItem) => {
        fetch("/api/administrator/setting",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Created item successfully");

                getSettings();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const updateSetting = (dataItem) => {
        fetch("/api/administrator/setting",
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Updated item successfully");

                getSettings();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const deleteSetting = (settingKey) => {
        const dataItem = {
            "settingKey": settingKey
        }

        fetch("/api/administrator/setting",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getSettings();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            edit={enterEdit}
            remove={remove}
            add={add}
            discard={discard}
            update={update}
            cancel={cancel}
            editField={editField}
            keyField="settingKey"
        />
    );

    const MyDropDownCell = (props) => {
        const data = settingTypes.map(obj => {
            return {
                value: obj.settingTypeId,
                text: obj.name
            };
        });

        return (
            <DropDownCell
                {...props}
                data={data}
                textField="settingTypeName"
                valueField="settingTypeId"
                onChange={itemChange}
            />
        );
    };

    const remove = (dataItem) => {
        const isEditingSetting = settings.some(
            (p) => p.inEdit === true
        );

        if (isEditingSetting === true) {
            addNotificationWithTimeout(dispatch, "error", "You can't remove an item while in edit mode");
            return;
        }

        deleteSetting(dataItem.settingKey);
    };

    const add = (dataItem) => {
        addSetting(dataItem);
    };

    const update = (dataItem) => {
        updateSetting(dataItem);
    };

    const discard = (dataItem) => {
        const newSettings = [...settings];
        newSettings.splice(0, 1);
        setSettings(newSettings);
    };

    const cancel = (dataItem) => {
        const originalItem = originalSettings.find(
            (p) => p.settingKey === dataItem.settingKey
        );

        const newSettings = settings.map((item) =>
            item.settingKey === dataItem.settingKey ? originalItem : item
        );
        setSettings(newSettings);
    };

    const enterEdit = (dataItem) => {
        const isEditingSetting = settings.some(
            (p) => p.inEdit === true
        );

        if (isEditingSetting === true) {
            addNotificationWithTimeout(dispatch, "error", "You can only edit one item at a time");
            return;
        }

        setOriginalSettings(settings);

        const newSettings = settings.map((item) =>
            item.settingKey === dataItem.settingKey ? { ...item, inEdit: true } : item
        );
        setSettings(newSettings);
    };

    const itemChange = (event) => {
        const field = event.field || "";
        const newSettings = settings.map((item) =>
            item.settingKey === event.dataItem.settingKey
                ? { ...item, [field]: event.value }
                : item
        );
        setSettings(newSettings);
    };

    const addNew = () => {
        const hasNewSetting = settings.some(
            (p) => p.settingKey === undefined
        );

        if (hasNewSetting === true) {
            addNotificationWithTimeout(dispatch, "error", "You can only add one item at a time");
            return;
        }

        const isEditingSetting = settings.some(
            (p) => p.inEdit === true
        );

        if (isEditingSetting === true) {
            addNotificationWithTimeout(dispatch, "error", "You can't add an item while editing another");
            return;
        }

        const newSetting = {
            inEdit: true,
        };
        setSettings([newSetting, ...settings]);
    };

    useEffect(() => {
        document.title = "System Settings";

        getSettingTypes();
    }, []);

    return (
        <>
            { areSettingsLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <div>
                    <Grid
                        data={settings}
                        onItemChange={itemChange}
                        editField={editField}
                        dataItemKey={"settingKey"}
                        resizable={true}
                        scrollable="none"
                    >
                        <GridToolbar>
                            <Button
                                title={"Add New"}
                                themeColor={"primary"}
                                icon="plus"
                                onClick={addNew}
                            >
                                Add New
                            </Button>
                        </GridToolbar>
                        <Column field="settingTypeName" title={"Type"} cell={MyDropDownCell} />
                        <Column field="value" title={"Value"} />
                        <Column cell={MyCommandCell} width="200px" />
                    </Grid>
                </div>
                ) : <div></div>)
            }
        </>
    );
}